import React, { useEffect, useMemo, useRef, forwardRef, useState } from 'react'
import cx from 'classnames'
import { path, is, pathOr, reverse, sort } from 'ramda'
import { FixedSizeList, VariableSizeList } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import Checkbox from '../../atoms/Checkbox/Checkbox'
import Table from './Table'
import useTable from './useTable'
import styles from './Table.module.scss'
import SortButton from './SortButton/SortButton'
import { $TSFixMe } from 'types/ts-migrate'

interface IAggregateTableRowProps {
  enableSelection?: boolean
  rowHeight?: number
  data: {
    rowData: readonly any[]
    onRowClick?: any
    interactive?: boolean
    columns?: any
    selection?: any
  }
  index: number
  style: any
}

const AggregateTableRow = ({ data, index, style }: IAggregateTableRowProps): React.ReactElement =>
  useMemo(() => {
    const { rowData, onRowClick, columns, selection, interactive } = data
    const handleClick =
      typeof onRowClick === 'function'
        ? (e: any) => {
            if (path(['target', 'type'], e) !== 'checkbox') {
              onRowClick(rowData[index])
            }
          }
        : () => {}

    const row = rowData[index]
    const classes = cx({
      // @ts-expect-error ts-migrate(2774) FIXME: This condition will always return true since the f... Remove this comment to see the full error message
      [styles.interactive]: handleClick && interactive,
      [styles.selected]: selection.includes(row.id),
      [styles.highlighted]: path(['highlighted'], row),
      [styles['animate-danger']]: path(['animateDanger'], row),
      [styles['animate-danger-flash']]: path(['animateDangerFlash'], row),
      [styles['animate-success']]: path(['animateSuccess'], row),
      [styles.error]: path(['hasError'], row),
    })
    return (
      <Table.Row className={classes} key={index} style={style} onClick={handleClick}>
        {columns.map((col: any, i: any) => (
          <Table.Cell
            key={i}
            style={col.style || {}}
            title={is(Object, row[col.accessor]) ? pathOr('', ['props', 'name'], row[col.accessor]) : row[col.accessor]}
          >
            {row[col.accessor]}
          </Table.Cell>
        ))}
      </Table.Row>
    )
  }, [data, index, style])

/**
 * Adds padding to the bottom of the list which prevents
 * FABs overlapping the bottom row.
 * @link https://github.com/CastleOne/robin-client/issues/465
 */
// @ts-expect-error ts-migrate(2339) FIXME: Property 'style' does not exist on type '{ childre... Remove this comment to see the full error message
const innerElementType = forwardRef(function ElementType({ style, ...rest }, ref) {
  return (
    <div
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'ForwardedRef<unknown>' is not assignable to ... Remove this comment to see the full error message
      ref={ref}
      style={{
        ...style,
        height: `${parseFloat(style.height) + 96}px`,
      }}
      {...rest}
    />
  )
})

/**
 * AggregateTableBody
 */
interface IAggregateTableBodyProps {
  tableData: readonly any[]
  onRowClick?: any
  rowHeight?: number
  getRowHeight?: (row: any) => number
  enableSelection?: boolean
  selection?: any
  setSelection?: any
  columns?: any
  interactive?: boolean
}

const AggregateTableBody = ({
  tableData,
  onRowClick,
  rowHeight,
  getRowHeight,
  enableSelection,
  selection,
  setSelection,
  columns,
  interactive,
}: IAggregateTableBodyProps): React.ReactElement => {
  // A variable-size list caches row heights, so they're remeasured whenever the rows change.
  const variableListRef = useRef<VariableSizeList>(null)
  useEffect(() => {
    variableListRef.current?.resetAfterIndex(0)
  }, [tableData, getRowHeight])

  const toggleSelection = (id: any): void => {
    let _selection = [...selection]
    if (_selection.includes(id)) {
      _selection = _selection.filter(s => s !== id)
    } else {
      _selection = [..._selection, id]
    }
    setSelection(_selection)
  }

  const itemData = useMemo(
    () => ({
      onRowClick,
      selection,
      rowHeight,
      columns: enableSelection
        ? [
            {
              Header: '',
              accessor: 'selected',
            },
            ...columns,
          ]
        : columns,
      rowData: enableSelection
        ? tableData.map((d: any) => ({
            selected: (
              <Checkbox
                disabled={d?.enableSelection === false}
                name="select"
                checked={selection.includes(d.id)}
                onChange={(e: any) => {
                  toggleSelection(d.id)
                }}
              />
            ),

            ...d,
          }))
        : tableData,
      interactive,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [tableData, onRowClick, selection],
  )

  return (
    <Table.Body>
      <AutoSizer>
        {({ height, width }: any) => {
          const listProps = {
            itemCount: tableData.length,
            itemData,
            height,
            width,
            innerElementType,
          }
          return getRowHeight ? (
            <VariableSizeList
              {...listProps}
              ref={variableListRef}
              itemSize={(index: number) => getRowHeight(tableData[index])}
              estimatedItemSize={rowHeight}
            >
              {AggregateTableRow}
            </VariableSizeList>
          ) : (
            <FixedSizeList {...listProps} itemSize={rowHeight as number}>
              {AggregateTableRow}
            </FixedSizeList>
          )
        }}
      </AutoSizer>
    </Table.Body>
  )
}

/**
 * The column a table opens on, or the one the user last clicked. A column sorts on
 * `meta[key]`, which defaults to its accessor but can be overridden — a column that renders
 * two values (a date range, say) sorts on whichever of them is active.
 */
export type TSortState = { key: string; order: 'asc' | 'desc' } | null

const getSortKey = (col: $TSFixMe): string => col.sort?.key ?? col.accessor

/**
 * A column opens the table sorted by declaring `sort: { enabled: true, order: 'asc' }`.
 * Anything that leaves `order` false starts unsorted, as before.
 */
const getDefaultSort = (columns: $TSFixMe[]): TSortState => {
  const defaultColumn = columns.find((col: $TSFixMe) => col.sort?.enabled && col.sort?.order)
  return defaultColumn ? { key: getSortKey(defaultColumn), order: defaultColumn.sort.order } : null
}

/**
 * What a column's `renderHeader` is handed, so it can draw its own sort affordance — the
 * leases table uses it for a lease term header that sorts on either the start or the end date.
 */
export type TSortableHeaderProps = {
  /** The key the table is currently sorted on, which may be none of this column's own. */
  sortKey: string | null
  sortOrder: false | 'asc' | 'desc'
  onSort: (key: string) => void
}

/**
 * AggregateTable
 */
export interface IAggregateTableProps {
  enableSelection?: boolean
  hideHeaderSelection?: boolean
  rowHeight?: number
  /** Per-row heights, for tables where some rows need more room; `rowHeight` is then just the estimate. */
  getRowHeight?: (row: any) => number
  columns?: any
  data: readonly any[]
  onRowClick?: (row: any) => void
  interactive?: boolean
  initialSelection?: any
  onSelectionChange?: Function
  index?: any
  /**
   * Server-side sorting. With `onSortChange` set, header clicks report the next sort instead of
   * sorting the rows, which are shown in the order given; `sort` is what the headers display.
   */
  sort?: TSortState
  onSortChange?: (sort: NonNullable<TSortState>) => void
}

const AggregateTable = ({
  enableSelection,
  hideHeaderSelection,
  rowHeight = 55,
  getRowHeight,
  columns,
  data,
  onRowClick,
  interactive = true,
  initialSelection,
  onSelectionChange,
  sort: serverSort,
  onSortChange,
}: IAggregateTableProps): null | React.ReactElement => {
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'selectAll' does not exist on type '{}'.
  const { selectAll, toggleAll, setSelectAll, selection = [], setSelection } = useTable()
  const [tableData, setTableData] = useState(data)

  const defaultSort = getDefaultSort(columns)
  const [localSort, setLocalSort] = useState<TSortState>(defaultSort)
  const isServerSorted = typeof onSortChange === 'function'
  const activeSort = isServerSorted ? serverSort ?? null : localSort

  const handleSortClick = (key: string): void => {
    // Clicking the column already being sorted on reverses it; anything else starts on the
    // column's `sort.firstOrder`, ascending unless it says otherwise.
    const firstOrder: 'asc' | 'desc' =
      columns.find((col: $TSFixMe) => getSortKey(col) === key)?.sort?.firstOrder ?? 'asc'
    const next = (current: TSortState): NonNullable<TSortState> => ({
      key,
      order: current?.key === key ? (current.order === 'asc' ? 'desc' : 'asc') : firstOrder,
    })
    if (isServerSorted) {
      onSortChange(next(activeSort))
    } else {
      setLocalSort(next)
    }
  }

  // Columns come and go between tabs, and the default sort goes with them — the leases table
  // drops its status column on a status tab and falls back to sorting by lease end date. Keyed on
  // the default itself rather than on `columns`, so a re-created column array doesn't throw away
  // the sort the user picked.
  useEffect(() => {
    setLocalSort(current =>
      current?.key === defaultSort?.key && current?.order === defaultSort?.order ? current : defaultSort,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSort?.key, defaultSort?.order])

  const sortTableData = ({ key, order }: NonNullable<TSortState>) => {
    const sortedAsc = sort((a, b) => {
      const valA = pathOr('', ['meta', key], a)
      const valB = pathOr('', ['meta', key], b)
      return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' })
    }, tableData)
    return order === 'asc' ? sortedAsc : reverse(sortedAsc)
  }

  // Reset selection when data changes, likely a filter change
  useEffect(() => {
    setSelection([])
    setSelectAll(false)
    setTableData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    toggleAll(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll])

  useEffect(() => {
    setSelection(initialSelection)
  }, [initialSelection, setSelection, data])

  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange(selection)
    }
  }, [selection, onSelectionChange])

  const cols = enableSelection
    ? [
        hideHeaderSelection
          ? {
              Header: <></>,
              accessor: 'selected',
            }
          : {
              Header: <Checkbox checked={selectAll} name="selectAll" onChange={() => setSelectAll(!selectAll)} />,
              accessor: 'selected',
            },
        ...columns,
      ]
    : columns

  const classes = cx({
    [styles['is-selectable']]: enableSelection,
  })

  const sortedTableData = activeSort && !isServerSorted ? sortTableData(activeSort) : tableData

  return data.length === 0 ? null : (
    <Table className={classes}>
      <Table.Head>
        <Table.Row>
          {cols.map((col: any, i: any) => {
            const key = getSortKey(col)
            const order = activeSort?.key === key ? activeSort.order : false

            return (
              <Table.Cell key={i} head style={col.style || {}}>
                {col.renderHeader ? (
                  col.renderHeader({
                    sortKey: activeSort?.key ?? null,
                    sortOrder: activeSort?.order ?? false,
                    onSort: handleSortClick,
                  })
                ) : (
                  <>
                    {col.Header}
                    {col.sort?.enabled && <SortButton onClick={() => handleSortClick(key)} order={order} />}
                  </>
                )}
              </Table.Cell>
            )
          })}
        </Table.Row>
      </Table.Head>

      <AggregateTableBody
        rowHeight={rowHeight}
        getRowHeight={getRowHeight}
        enableSelection={enableSelection}
        selection={selection}
        setSelection={setSelection}
        columns={columns}
        interactive={interactive}
        onRowClick={onRowClick}
        tableData={sortedTableData}
      />
    </Table>
  )
}

export default AggregateTable
